import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router";
import { Id } from "@convex/_generated/dataModel";
import { BlockCard } from "./block-card";
import { BlockFormSheet } from "./block-form-sheet";
import { DeleteDialog } from "./delete-dialog";
import { SwipeableCard } from "@/components/ui/swipeable-card";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import { format, parseISO, isToday, isYesterday } from "date-fns";
import { Button } from "@/components/ui/button";
import { HistoryFilterSheet, HistoryFilters } from "./history-filter-sheet";
import { formatCurrency } from "@/lib/format";

interface BlockWithShift {
  _id: Id<"blocks">;
  date: string;
  shiftStart: string;
  shiftEnd: string;
  basePay: number;
  tip: number;
  noRoutePayout: number;
  station?: string;
  manualMiles?: number;
  routeRating?: number;
  overbooked?: boolean;
  shift?: {
    totalMiles: number;
    status: string;
    startedAt?: number;
    endedAt?: number;
    pauseDuration?: number;
  } | null;
}

interface BlockListViewProps {
  blocks: BlockWithShift[] | undefined;
  isLoading?: boolean;
  showFilters?: boolean;
  emptyMessage?: string;
  pageSize?: number;
}

export function BlockListView({
  blocks,
  isLoading = false,
  showFilters = true,
  emptyMessage = "No blocks found",
  pageSize = 20,
}: BlockListViewProps) {
  const navigate = useNavigate();
  const [editBlockId, setEditBlockId] = useState<Id<"blocks"> | null>(null);
  const [deleteBlockId, setDeleteBlockId] = useState<Id<"blocks"> | null>(null);
  const [filters, setFilters] = useState<HistoryFilters>({});
  const [displayLimit, setDisplayLimit] = useState(pageSize);

  // Extract unique stations from blocks
  const stations = useMemo(() => {
    if (!blocks) return [];
    const stationSet = new Set<string>();
    blocks.forEach((b) => {
      if (b.station) stationSet.add(b.station);
    });
    return Array.from(stationSet).sort();
  }, [blocks]);

  // Find the block date for delete dialog
  const deleteBlock = blocks?.find((b) => b._id === deleteBlockId);
  const deleteBlockDate = deleteBlock
    ? format(parseISO(deleteBlock.date), "EEE, MMM d")
    : "";

  // Apply filters
  const filteredBlocks = useMemo(() => {
    if (!blocks) return [];
    return blocks.filter((block) => {
      // Date range filter
      if (filters.dateFrom && block.date < filters.dateFrom) return false;
      if (filters.dateTo && block.date > filters.dateTo) return false;

      // Station filter
      if (filters.station && block.station !== filters.station) return false;

      // Earnings filter
      const total = block.basePay + block.tip + block.noRoutePayout;
      if (filters.minEarnings && total < filters.minEarnings) return false;
      if (filters.maxEarnings && total > filters.maxEarnings) return false;

      // Rating filter
      if (filters.rating !== undefined) {
        if (filters.rating === "unrated") {
          if (block.routeRating !== undefined) return false;
        } else {
          if (!block.routeRating || block.routeRating < filters.rating) return false;
        }
      }

      // Duration filter
      if (filters.duration) {
        const [startH, startM] = block.shiftStart.split(":").map(Number);
        const [endH, endM] = block.shiftEnd.split(":").map(Number);
        const durationMins = (endH * 60 + endM) - (startH * 60 + startM);
        // Match exact duration with 15-minute tolerance
        if (Math.abs(durationMins - filters.duration) > 15) return false;
      }

      return true;
    });
  }, [blocks, filters]);

  // Paginate displayed blocks
  const displayedBlocks = useMemo(() => {
    return filteredBlocks.slice(0, displayLimit);
  }, [filteredBlocks, displayLimit]);

  // Group blocks by date
  const groupedBlocks = useMemo(() => {
    const groups: Map<string, { blocks: BlockWithShift[]; total: number }> = new Map();

    for (const block of displayedBlocks) {
      const existing = groups.get(block.date);
      const blockTotal = block.basePay + block.tip + block.noRoutePayout;

      if (existing) {
        existing.blocks.push(block);
        existing.total += blockTotal;
      } else {
        groups.set(block.date, { blocks: [block], total: blockTotal });
      }
    }

    return groups;
  }, [displayedBlocks]);

  // Format date for day header
  const formatDayHeader = (dateStr: string): string => {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Today";
    if (isYesterday(date)) return "Yesterday";
    return format(date, "EEEE, MMM d");
  };

  const hasMoreToShow = filteredBlocks.length > displayLimit;

  const handleLoadMore = useCallback(() => {
    setDisplayLimit((prev) => prev + pageSize);
  }, [pageSize]);

  const handleRefresh = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 500));
  }, []);

  // Loading state
  if (isLoading || blocks === undefined) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  // Empty state
  if (blocks.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  // Filtered empty state
  if (filteredBlocks.length === 0 && blocks.length > 0) {
    return (
      <>
        {showFilters && (
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm text-muted-foreground">0 blocks</span>
            <HistoryFilterSheet
              filters={filters}
              onFiltersChange={setFilters}
              stations={stations}
            />
          </div>
        )}
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">No blocks match your filters.</p>
          <Button variant="outline" onClick={() => setFilters({})}>
            Clear Filters
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <PullToRefresh onRefresh={handleRefresh}>
        <div>
          {showFilters && (
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-muted-foreground">
                {filteredBlocks.length} {filteredBlocks.length === 1 ? "block" : "blocks"}
              </span>
              <HistoryFilterSheet
                filters={filters}
                onFiltersChange={setFilters}
                stations={stations}
              />
            </div>
          )}
          <div className="space-y-4">
            {Array.from(groupedBlocks.entries()).map(([date, { blocks: dayBlocks, total }]) => (
              <div key={date}>
                {/* Day Header */}
                <div className="flex items-center justify-between px-1 py-2 mb-2 border-b border-border/50">
                  <span className="font-medium text-sm">{formatDayHeader(date)}</span>
                  <span className="text-sm text-muted-foreground">{formatCurrency(total)}</span>
                </div>
                {/* Blocks for this day */}
                <div className="space-y-2">
                  {dayBlocks.map((block) => (
                    <SwipeableCard
                      key={block._id}
                      onTap={() => navigate(`/blocks/${block._id}`)}
                      onSwipeRight={() => setEditBlockId(block._id)}
                      onSwipeLeft={() => setDeleteBlockId(block._id)}
                    >
                      <BlockCard block={block} />
                    </SwipeableCard>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {hasMoreToShow && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={handleLoadMore} className="w-full max-w-xs">
                Load More
              </Button>
            </div>
          )}
        </div>
      </PullToRefresh>

      <BlockFormSheet
        mode="edit"
        blockId={editBlockId!}
        trigger={<span />}
        open={editBlockId !== null}
        onOpenChange={(open) => !open && setEditBlockId(null)}
      />

      <DeleteDialog
        blockId={deleteBlockId!}
        blockDate={deleteBlockDate}
        open={deleteBlockId !== null}
        onOpenChange={(open) => !open && setDeleteBlockId(null)}
      />
    </>
  );
}
